import { defineMessages } from 'react-intl';

const moveInMessages = defineMessages({
  title: {
    id: 'App.EditMoveDateModal.MoveIn.Title',
    defaultMessage: 'Edit Move-In Date',
  },
  dateLabel: {
    id: 'App.EditMoveDateModal.MoveIn.DateLabel',
    defaultMessage: 'Move-In Date',
  },
  currentDate: {
    id: 'App.EditMoveDateModal.MoveIn.CurrentDate',
    defaultMessage: 'Current: {date}',
  },
  // A stay opened from the Unit History: the title names its unit.
  titleForUnit: {
    id: 'App.EditMoveDateModal.MoveIn.TitleForUnit',
    defaultMessage: 'Edit Move-In Date - Unit {unitLabel}',
  },
});

// The affected-record list of the Move-In Date correction (household-mi-mo-date-changes).
const impactMessages = defineMessages({
  reviewBanner: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.ReviewBanner',
    defaultMessage:
      'Changing the Move-In Date will update the records below. Review them before saving.',
  },
  recordsAffected: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.RecordsAffected',
    defaultMessage:
      '{count, plural, one {# record will be affected} other {# records will be affected}}',
  },
  certificationTitle: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.CertificationTitle',
    defaultMessage: '{programName} · {typeName}',
  },
  certificationTitleWithDate: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.CertificationTitleWithDate',
    defaultMessage: '{programName} · {typeName} · Effective {date}',
  },
  alignDescription: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.AlignDescription',
    defaultMessage: 'Effective date will update to {date}.',
  },
  hudCorrectionDescription: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.HudCorrectionDescription',
    defaultMessage: 'A HUD correction will start automatically.',
  },
  hudReopenDescription: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.HudReopenDescription',
    defaultMessage:
      'The certification will be reopened with effective date {date}.',
  },
  formDetail: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.FormDetail',
    defaultMessage: 'Document: {formName}',
  },
  needsReviewDescription: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.NeedsReviewDescription',
    defaultMessage:
      'Move-In Date changed. Review and regenerate form {formName}.',
  },
  originMoveOutTitle: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.OriginMoveOutTitle',
    defaultMessage: 'Prior Unit {unitNumber}',
  },
  originMoveOutDescription: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.OriginMoveOutDescription',
    defaultMessage:
      'Move-Out Date will change from {currentDate} to {date}, the transfer date.',
  },
  activeLeaseTitle: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.ActiveLeaseTitle',
    defaultMessage: 'Active Lease',
  },
  activeLeaseDescription: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.ActiveLeaseDescription',
    defaultMessage: 'Scheduled Move-In Date will change to {date}.',
  },
  stayLeaseTitle: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.StayLeaseTitle',
    defaultMessage: 'Lease (started {date})',
  },
  stayLeaseDescription: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.StayLeaseDescription',
    defaultMessage:
      'Scheduled Move-In Date will change from {currentDate} to {date}.',
  },
  correctedLeaseRecommended: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.CorrectedLeaseRecommended',
    defaultMessage:
      "Corrected Lease recommended. Upload the corrected lease now or later from the resident's Documents tab.",
  },
  // ENG-33327: the Active Lease row when the user can upload the Corrected Lease in the modal.
  activeLeaseUploadDescription: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.ActiveLeaseUploadDescription',
    defaultMessage:
      'Scheduled Move-In Date will update to {date}. ' +
      "Upload the Corrected Lease now or later under the Resident's Documents tab.",
  },
  activeLeaseUploadedDescription: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.ActiveLeaseUploadedDescription',
    defaultMessage: 'Scheduled Move-In Date will update to {date}.',
  },
  uploadCorrectedLease: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.UploadCorrectedLease',
    defaultMessage: 'Upload',
  },
  editUploadedCorrectedLease: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.EditUploadedCorrectedLease',
    defaultMessage: 'Edit uploaded',
  },
  uploaded: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.Uploaded',
    defaultMessage: 'Uploaded',
  },
  autoUpdated: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.AutoUpdated',
    defaultMessage: 'Auto-updated',
  },
  hudCorrectionChip: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.HudCorrectionChip',
    defaultMessage: 'HUD correction will start',
  },
  hudReopenChip: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.HudReopenChip',
    defaultMessage: 'HUD reopen',
  },
  needsReview: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.NeedsReview',
    defaultMessage: 'Needs review',
  },
  actionNeeded: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.ActionNeeded',
    defaultMessage: 'Action needed',
  },
  loadError: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.LoadError',
    defaultMessage: 'The affected records could not be loaded.',
  },
  tryAgain: {
    id: 'App.EditMoveDateModal.MoveIn.Impacts.TryAgain',
    defaultMessage: 'Try again',
  },
});

const moveOutMessages = defineMessages({
  title: {
    id: 'App.EditMoveDateModal.MoveOut.Title',
    defaultMessage: 'Edit Move-Out Date',
  },
  dateLabel: {
    id: 'App.EditMoveDateModal.MoveOut.DateLabel',
    defaultMessage: 'Move-Out Date',
  },
  currentDate: {
    id: 'App.EditMoveDateModal.MoveOut.CurrentDate',
    defaultMessage: 'Current: {date}',
  },
  rdWarning: {
    id: 'App.EditMoveDateModal.MoveOut.RDWarning',
    defaultMessage:
      'This is an RD unit, editing this date will update the effective date of the Vacate cert. ' +
      'Complete the certification to transmit to MINC.',
  },
});

// The Upload Corrected Lease dialog of the Move-In Date correction (ENG-33327).
const correctedLeaseUploadMessages = defineMessages({
  title: {
    id: 'App.EditMoveDateModal.CorrectedLeaseUpload.Title',
    defaultMessage: 'Upload Corrected Lease',
  },
  intro: {
    id: 'App.EditMoveDateModal.CorrectedLeaseUpload.Intro',
    defaultMessage:
      "Upload the Corrected Lease for the updated Move-In Date. The file is saved to the resident's Documents.",
  },
  fileTooLarge: {
    id: 'App.EditMoveDateModal.CorrectedLeaseUpload.FileTooLarge',
    defaultMessage: '{fileName} is larger than {maxSizeInMB} MB.',
  },
});

// The affected-record list of the Move-Out Date correction (household-mi-mo-date-changes). The
// rows it shares with the Move-In list (certification titles, actions, chips) use impactMessages.
const moveOutImpactMessages = defineMessages({
  reviewBanner: {
    id: 'App.EditMoveDateModal.MoveOut.Impacts.ReviewBanner',
    defaultMessage:
      'Changing the Move-Out Date will update the records below. Review them before saving.',
  },
  // A completed Move-Out cert (MO, TM, Vacate) of a program without a HUD correction flow.
  needsReviewDescription: {
    id: 'App.EditMoveDateModal.MoveOut.Impacts.NeedsReviewDescription',
    defaultMessage:
      'Move-Out Date changed. Review and regenerate form {formName}.',
  },
  // A completed cert effective on or after the corrected (earlier) Move-Out Date.
  needsReviewAfterMoveOutDescription: {
    id: 'App.EditMoveDateModal.MoveOut.Impacts.NeedsReviewAfterMoveOutDescription',
    defaultMessage:
      'Effective after the corrected Move-Out Date. Review form {formName}.',
  },
  // A lease of the stay starting after the corrected Move-Out Date: listed, never changed.
  affectedLeaseDescription: {
    id: 'App.EditMoveDateModal.MoveOut.Impacts.AffectedLeaseDescription',
    defaultMessage:
      'Starts after the corrected Move-Out Date. Its dates will not change.',
  },
});

export {
  correctedLeaseUploadMessages,
  impactMessages,
  moveInMessages,
  moveOutImpactMessages,
  moveOutMessages,
};
